import { FAQData, FAQGroup, FAQItem } from '../components/faq/types';

// Matches FAQ link placeholders like:
//  - #link:group:question# => becomes clickable link to expand that question
//  - #link:group# => becomes clickable link to expand that group
export const FAQ_LINK_PLACEHOLDER_REGEX = /#link:([^:#]+)(?::([^#]+))?#/g;

export interface FAQLinkInfo {
  group: string;
  question?: string;
  originalText: string;
}

export interface ProcessedFAQContent {
  html: string;
  links: FAQLinkInfo[];
}

/**
 * Process FAQ content to extract link information and convert placeholders to clickable links
 */
export const processFAQContent = (content: string): ProcessedFAQContent => {
  const links: FAQLinkInfo[] = [];

  const processedHtml = content.replace(FAQ_LINK_PLACEHOLDER_REGEX, (match, group, question) => {
    const linkInfo: FAQLinkInfo = {
      group: group.trim(),
      question: question ? question.trim() : undefined,
      originalText: match,
    };
    links.push(linkInfo);

    const linkText = question ? `${group}: ${question}` : group;
    const dataAttributes = `data-faq-group="${group}" ${question ? `data-faq-question="${question}"` : ''}`;

    return `<a href="#" class="faq-link" ${dataAttributes} style="color: #1976d2; text-decoration: underline; cursor: pointer;">${linkText}</a>`;
  });

  return {
    html: processedHtml,
    links,
  };
};

/**
 * Find a group by name in FAQ data
 */
export const findGroup = (data: FAQData, groupName: string): FAQGroup | undefined => {
  return data.groups.find((group) => group.group === groupName);
};

/**
 * Find a question within a group
 */
export const findQuestion = (group: FAQGroup, questionText: string): FAQItem | undefined => {
  return group.items.find((item) => item.question === questionText);
};

/**
 * Generate a unique ID for a question based on group and question text
 */
export const generateQuestionId = (groupName: string, questionText: string): string => {
  return `faq-${groupName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-${questionText
    .slice(0, 30)
    .replace(/[^a-zA-Z0-9]/g, '-')
    .toLowerCase()}`;
};

/**
 * Generate a unique ID for a group
 */
export const generateGroupId = (groupName: string): string => {
  return `faq-group-${groupName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`;
};
